import React, { useState, useEffect } from 'react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../ui/dialog';
import { Card, CardContent } from '../../ui/card';
import { toast } from 'sonner';
import { 
  Plus, Trash2, GripVertical, Edit2, Check, X, 
  Palette, ArrowUp, ArrowDown 
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const DEFAULT_COLORS = [
  '#3b82f6', '#06b6d4', '#10b981', '#f59e0b', '#8b5cf6', 
  '#22c55e', '#ef4444', '#ec4899', '#14b8a6', '#f97316'
];

export const StageManagerDialog = ({ open, onClose, stages, onStagesUpdated }) => {
  const [localStages, setLocalStages] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [newStageName, setNewStageName] = useState('');
  const [newStageColor, setNewStageColor] = useState(DEFAULT_COLORS[0]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && stages) {
      setLocalStages([...stages].sort((a, b) => a.order - b.order));
    }
  }, [open, stages]);

  const handleAddStage = async () => {
    if (!newStageName.trim()) {
      toast.error('Please enter a stage name');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const slug = newStageName.toLowerCase().replace(/\s+/g, '_');
      
      const response = await fetch(`${API}/api/crm/pipeline-stages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: newStageName.trim(),
          slug,
          color: newStageColor,
        }),
      });

      if (response.ok) {
        const newStage = await response.json();
        setLocalStages([...localStages, newStage]);
        setNewStageName('');
        setNewStageColor(DEFAULT_COLORS[(localStages.length + 1) % DEFAULT_COLORS.length]);
        toast.success('Stage added');
        onStagesUpdated && onStagesUpdated();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to add stage');
      }
    } catch (error) {
      toast.error('Failed to add stage');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateStage = async (stageId) => {
    if (!editName.trim()) {
      toast.error('Stage name cannot be empty');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/api/crm/pipeline-stages/${stageId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ name: editName.trim() }),
      });

      if (response.ok) {
        setLocalStages(localStages.map(s => 
          s.id === stageId ? { ...s, name: editName.trim() } : s
        ));
        setEditingId(null);
        toast.success('Stage updated');
        onStagesUpdated && onStagesUpdated();
      }
    } catch (error) {
      toast.error('Failed to update stage');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteStage = async (stageId) => {
    if (localStages.length <= 2) {
      toast.error('Must have at least 2 stages');
      return;
    }

    const stage = localStages.find(s => s.id === stageId);
    if (stage?.is_system) {
      toast.error('Cannot delete system stages');
      return;
    }

    if (!confirm('Delete this stage? Leads in this stage will need to be reassigned.')) {
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/api/crm/pipeline-stages/${stageId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        setLocalStages(localStages.filter(s => s.id !== stageId));
        toast.success('Stage deleted');
        onStagesUpdated && onStagesUpdated();
      }
    } catch (error) {
      toast.error('Failed to delete stage');
    } finally {
      setSaving(false);
    }
  };

  const handleMoveStage = async (stageId, direction) => {
    const index = localStages.findIndex(s => s.id === stageId);
    if (
      (direction === 'up' && index === 0) ||
      (direction === 'down' && index === localStages.length - 1)
    ) {
      return;
    }

    const newIndex = direction === 'up' ? index - 1 : index + 1;
    const newStages = [...localStages];
    [newStages[index], newStages[newIndex]] = [newStages[newIndex], newStages[index]];
    
    // Update order values
    newStages.forEach((s, i) => {
      s.order = i;
    });
    
    setLocalStages(newStages);

    // Save new order to backend
    try {
      const token = localStorage.getItem('token');
      await Promise.all(newStages.map((stage, i) => 
        fetch(`${API}/api/crm/pipeline-stages/${stage.id}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ order: i }),
        })
      ));
      onStagesUpdated && onStagesUpdated();
    } catch (error) {
      toast.error('Failed to reorder stages');
    }
  };

  const handleColorChange = async (stageId, color) => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API}/api/crm/pipeline-stages/${stageId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ color }),
      });

      setLocalStages(localStages.map(s => 
        s.id === stageId ? { ...s, color } : s
      ));
      onStagesUpdated && onStagesUpdated();
    } catch (error) {
      toast.error('Failed to update color');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Manage Pipeline Stages</DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-4 py-4">
          {/* Existing Stages */}
          <div className="space-y-2">
            {localStages.map((stage, index) => (
              <Card key={stage.id} className="overflow-hidden">
                <CardContent className="p-3">
                  <div className="flex items-center gap-2">
                    {/* Drag Handle / Order Buttons */}
                    <div className="flex flex-col gap-0.5">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-5 w-5"
                        onClick={() => handleMoveStage(stage.id, 'up')}
                        disabled={index === 0}
                      >
                        <ArrowUp className="h-3 w-3" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-5 w-5"
                        onClick={() => handleMoveStage(stage.id, 'down')}
                        disabled={index === localStages.length - 1}
                      >
                        <ArrowDown className="h-3 w-3" />
                      </Button>
                    </div>

                    {/* Color Picker */}
                    <input
                      type="color"
                      value={stage.color || '#3b82f6'}
                      onChange={(e) => handleColorChange(stage.id, e.target.value)}
                      className="w-6 h-6 rounded cursor-pointer border-0"
                      title="Change color"
                    />

                    {/* Stage Name */}
                    <div className="flex-1">
                      {editingId === stage.id ? (
                        <div className="flex items-center gap-1">
                          <Input
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                            className="h-8"
                            autoFocus
                            onKeyPress={(e) => e.key === 'Enter' && handleUpdateStage(stage.id)}
                          />
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => handleUpdateStage(stage.id)}
                          >
                            <Check className="h-4 w-4 text-green-600" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => setEditingId(null)}
                          >
                            <X className="h-4 w-4 text-red-600" />
                          </Button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{stage.name}</span>
                          {stage.is_system && (
                            <span className="text-xs bg-muted px-1.5 py-0.5 rounded">System</span>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    {editingId !== stage.id && (
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => {
                            setEditingId(stage.id);
                            setEditName(stage.name);
                          }}
                        >
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        {!stage.is_system && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-red-600 hover:text-red-700"
                            onClick={() => handleDeleteStage(stage.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Add New Stage */}
          <Card className="border-dashed">
            <CardContent className="p-3">
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={newStageColor}
                  onChange={(e) => setNewStageColor(e.target.value)}
                  className="w-6 h-6 rounded cursor-pointer border-0"
                />
                <Input
                  value={newStageName}
                  onChange={(e) => setNewStageName(e.target.value)}
                  placeholder="New stage name..."
                  className="flex-1 h-9"
                  onKeyPress={(e) => e.key === 'Enter' && handleAddStage()}
                />
                <Button
                  size="sm"
                  onClick={handleAddStage}
                  disabled={saving || !newStageName.trim()}
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default StageManagerDialog;
